class PopsyCardSizeSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.variants = this.#readVariants();
    this.sizeOptionIndex = Number(this.dataset.sizeOptionPosition || 1) - 1;
    this.addEventListener('click', this.#onClick);
    this.#syncSelectedVariant();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #readVariants() {
    const template = this.querySelector('template[data-variants-json]');
    if (!(template instanceof HTMLTemplateElement)) return [];
    try {
      return JSON.parse(template.content.textContent || '[]');
    } catch {
      return [];
    }
  }

  #onClick = (event) => {
    const option = event.target.closest('.popsy-card-size-selector__option');
    if (!(option instanceof HTMLButtonElement) || option.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const optionIndex = Number(option.dataset.optionPosition || 1) - 1;
    const selections = this.#getSelections();
    selections[optionIndex] = option.dataset.optionValue || '';

    let variant = this.#findVariant(selections, true);
    if (!variant) {
      variant = this.variants.find(
        (candidate) => candidate.available && candidate.options?.[optionIndex] === selections[optionIndex]
      );
    }

    if (variant) this.#applyVariant(variant, true);
  };

  #getSelections() {
    const selections = [];
    this.querySelectorAll('.popsy-card-size-selector__option[aria-pressed="true"]').forEach((button) => {
      const index = Number(button.dataset.optionPosition || 1) - 1;
      selections[index] = button.dataset.optionValue || '';
    });
    return selections;
  }

  #findVariant(selections, availableOnly = false) {
    return this.variants.find((variant) => {
      if (availableOnly && !variant.available) return false;
      return selections.every((value, index) => !value || variant.options?.[index] === value);
    });
  }

  #syncSelectedVariant() {
    let variant = this.#findVariant(this.#getSelections(), true);
    if (!variant) variant = this.variants.find((candidate) => candidate.available);

    if (variant) {
      this.#applyVariant(variant, false);
      return;
    }

    this.#setAddToCartState(false);
    const card = this.closest('product-card');
    const variantInput = card?.querySelector('.buy-buttons-block product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = '';
  }

  #applyVariant(variant, updatePrice) {
    const card = this.closest('product-card');
    if (!card) return;

    this.querySelectorAll('.popsy-card-size-selector__option').forEach((button) => {
      const optionIndex = Number(button.dataset.optionPosition || 1) - 1;
      button.setAttribute('aria-pressed', String(variant.options?.[optionIndex] === button.dataset.optionValue));
    });

    this.#syncAvailability(variant);

    const variantInput = card.querySelector('.buy-buttons-block product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = String(variant.id || '');

    this.#setAddToCartState(Boolean(variant.available));
    this.#updateProductLinks(card, String(variant.id || ''));
    this.#updateCardImage(card);

    if (updatePrice) {
      const template = this.querySelector(`template[data-variant-price="${variant.id}"]`);
      const priceContainers = card.querySelectorAll('product-price [ref="priceContainer"]');
      const currentPrice = priceContainers[priceContainers.length - 1];
      const replacement = template?.content.firstElementChild?.cloneNode(true);
      if (currentPrice && replacement) currentPrice.replaceWith(replacement);
    }
  }

  #syncAvailability(selectedVariant) {
    this.querySelectorAll('.popsy-card-size-selector__option').forEach((button) => {
      const optionIndex = Number(button.dataset.optionPosition || 1) - 1;
      let available;

      if (optionIndex === this.sizeOptionIndex && this.variants.length > 1) {
        available = this.variants.some((candidate) => {
          if (!candidate.available || candidate.options?.[optionIndex] !== button.dataset.optionValue) return false;
          return candidate.options.every(
            (value, index) => index === optionIndex || value === selectedVariant.options?.[index]
          );
        });
      } else {
        available = this.variants.some(
          (candidate) => candidate.available && candidate.options?.[optionIndex] === button.dataset.optionValue
        );
      }

      button.disabled = !available;
      button.dataset.available = String(available);
      button.setAttribute('aria-disabled', String(!available));
      button.classList.toggle('is-sold-out', !available);
    });
  }

  #setAddToCartState(available) {
    const card = this.closest('product-card');
    const container = card?.querySelector('.buy-buttons-block product-form-component add-to-cart-component');
    const button = container?.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = !available;
    const label = button.querySelector('.add-to-cart-text__content');
    if (label) label.textContent = available ? this.dataset.addLabel || 'Add to cart' : this.dataset.soldOutLabel || 'Sold out';
  }

  #updateProductLinks(card, variantId) {
    if (!variantId) return;
    card.querySelectorAll('.product-card__link, [ref="productTitleLink"], [ref="cardGalleryLink"]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    });
  }

  #updateCardImage(card) {
    const selectedDesign = this.querySelector(
      '.popsy-card-size-selector__option[data-option-image-src][aria-pressed="true"]'
    );
    const imageSrc = selectedDesign?.dataset.optionImageSrc;
    if (!imageSrc) return;

    card.querySelectorAll('.card-gallery img').forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;
      image.src = imageSrc;
      image.srcset = imageSrc;
    });
  }
}

if (!customElements.get('popsy-card-size-selector')) {
  customElements.define('popsy-card-size-selector', PopsyCardSizeSelector);
}

